import { useEffect, useState } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import Tooltip from "@mui/material/Tooltip";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";
import EventOutlined from "@mui/icons-material/EventOutlined";
import { useSelector } from "react-redux";
import { RootState } from "../app/Store";
import { UserAccessType } from "../user/Models";
import { formatSubscriptionDate, subscriptionDaysRemaining } from "./SubscriptionDates";
import classes from "./SubscriptionNotice.module.css";

function getSubscriptionNoticeContent(days: number) {
  const dayUnit = days === 1 ? "day" : "days";
  const title = `Subscription expires in ${days} ${dayUnit}`;
  const compactTitle = `${days} ${dayUnit} left`;
  const expiresSoonMessage = "Subscription expires soon, contact SolarGik support";

  switch (true) {
    case days < 0:
      return {
        severity: "urgent",
        title: "Subscription has expired",
        compactTitle: "Expired",
        message: "Subscription has expired, service may be interrupted, contact SolarGik support",
      };
    case days === 0:
      return {
        severity: "urgent",
        title: "Subscription expires today",
        compactTitle: "Expires today",
        message: expiresSoonMessage,
      };
    case days <= 7:
      return { severity: "urgent", title, compactTitle, message: expiresSoonMessage };
    case days <= 30:
      return { severity: "warning", title, compactTitle, message: null };
    default:
      return {
        severity: "normal",
        title: "Subscription end date",
        compactTitle: "Subscription",
        message: null,
      };
  }
}

export default function SubscriptionNotice() {
  const siteId = useSelector((state: RootState) => state.site.siteId);
  const userType = useSelector((state: RootState) => state.user.userType);
  const subscription = useSelector((state: RootState) => state.subscriptions?.bySite[siteId]);
  const timeZone = useSelector((state: RootState) => state.site.ianaTimeZoneName);
  const isCompact = useMediaQuery("(max-width: 600px)");
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    setNow(new Date());
    const refresh = () => setNow(new Date());
    const timer = setInterval(refresh, 60000);
    window.addEventListener("focus", refresh);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", refresh);
    };
  }, [siteId]);

  if (
    userType >= UserAccessType.Admin ||
    subscription?.status !== "dated" ||
    !subscription.productExpirationDate
  ) {
    return null;
  }
  const days = subscriptionDaysRemaining(subscription.productExpirationDate, now, timeZone);
  const date = formatSubscriptionDate(subscription.productExpirationDate);
  const { severity, title, compactTitle, message } = getSubscriptionNoticeContent(days);
  const details = message ? `${date} · ${message}` : date;
  const Icon = severity === "normal" ? EventOutlined : WarningAmberRounded;

  return (
    <Tooltip
      title={
        <div className={classes["tooltip-content"]}>
          <strong>{title}</strong>
          <span>Subscription end date: {date}</span>
          {days >= 0 && days <= 30 && (
            <span>
              {days} {days === 1 ? "day" : "days"} remaining
            </span>
          )}
          {message && <span>{message}</span>}
        </div>
      }
      describeChild
      arrow
      placement="bottom-start"
      enterTouchDelay={0}
      classes={{ tooltip: `${classes.balloon} ${classes[severity]}`, arrow: classes.arrow }}
    >
      <div
        className={`${classes.notice} ${classes[severity]}`}
        role="status"
        aria-label="Subscription"
        tabIndex={0}
      >
        <Icon className={classes.icon} aria-hidden="true" />
        <div className={classes.copy}>
          <strong className={classes.title}>{isCompact ? compactTitle : title}</strong>
          <span className={classes.details}>{details}</span>
        </div>
      </div>
    </Tooltip>
  );
}
